interface PriceValueType {
  value: string | number;
  maintenanceCost?: boolean;
  type?: string;
}

/**
 * 금액 표기방식 - 일반표기
 * 억 단위 표시
 * 만 단위는 maintenanceCost가 true일 때만 표시
 *
 * 1억원 이상 -> ##억 #,###
 *
 * 1억원 미만 -> #,###
 *
 * @param {Object} param - 함수에 전달되는 매개변수 객체
 * @param {string|number} param.value - 변환할 숫자입니다. 문자열 또는 숫자 형태로 입력됩니다.
 * @param {boolean} [param.maintenanceCost=false] - 만 단위를 포함할지 여부를 나타내는 옵션입니다.
 * @returns {string} 주어진 숫자를 한글로 변환한 결과입니다.
 */
export function numberToNormalPrice({
  value,
  maintenanceCost = false,
  type,
}: PriceValueType): string {
  const number = Number(value) > 0 ? Number(value) : Math.abs(Number(value));

  if (isNaN(number) || number < 0) return '-';
  if (number === 0) {
    if (type === 'mnex') return '0';
    return '-';
  }

  const unitWords = ['', '억', '조', '경'];
  if (maintenanceCost) unitWords.splice(1, 0, '만');
  // if (maintenanceCost) unitWords[0] = '만';

  const splitUnit = 10000;
  let result = '';

  for (let i = 0; i < unitWords.length; i++) {
    const unitValue = Math.floor((number / Math.pow(splitUnit, i)) % splitUnit);
    if (unitValue) {
      result = `${formatWithCommas(unitValue)}${unitWords[i]} ${result}`;
    }
  }

  return Number(value) > 0 ? result.trim() : `-${result.trim()}`;
}

/**
 * 금액 표기방식 - 짧은표기
 * 억 단위 표시
 * 만 단위는 maintenanceCost가 true일 때만 표시
 * 천만 단위 소수점으로 출력 / 소수점 2자리까지 출력/ 십만 이하 절사
 *
 * 15억6천만원 -> 15.6억
 *
 * 6천만원 -> 0.6억
 *
 * @param {Object} param - 함수에 전달되는 매개변수 객체
 * @param {string|number} param.value - 변환할 숫자입니다. 문자열 또는 숫자 형태로 입력됩니다.
 * @param {boolean} [param.maintenanceCost=false] - 만 단위를 포함할지 여부를 나타내는 옵션입니다.
 * @returns {string} 주어진 숫자를 한글로 변환한 결과입니다.
 */
export function numberToShortPrice({ value, maintenanceCost = false }: PriceValueType): string {
  const number = Number(value);
  if (number < 100) return value + '만';

  if (isNaN(number) || number < 0) return '-';
  if (number === 0) return '-';

  const unitWords = ['', '억', '조', '경'];
  if (maintenanceCost) unitWords.splice(1, 0, '만');
  const decimalPoint = maintenanceCost ? 10 : 100;
  const splitUnit = 10000;
  let result = '';

  for (let i = unitWords.length - 1; i >= 0; i--) {
    const formula = (number / Math.pow(splitUnit, i)) % splitUnit;
    const unitValue =
      i > 1 ? Math.floor(formula) : Math.floor(formula * decimalPoint) / decimalPoint;

    if (unitValue && i > 0) {
      result = `${formatWithCommas(unitValue)}${unitWords[i]} ${result}`;
    }
  }

  return result.trim();
}

export function formatWithCommas(num: number): string {
  return num.toLocaleString('ko-KR');
}

export function meterToPeyong(isSquareMeter: Boolean, meter: number, ptp?: string): string {
  if (!meter) return '-';
  if (isSquareMeter) return formatWithCommas(meter)?.toString() + (ptp || '');
  else {
    const peyong = meter / 3.3058;
    const truncatedPeyong = Math.floor(peyong * 10) / 10; // 소수점 첫 번째 자리까지 표시하고 두 번째 자리 버림
    return truncatedPeyong.toString() + (ptp || '');
  }
}
